import React, { useRef, useState } from "react";
import { Heading } from "@/components/ui/Heading";
import { Text } from "@/components/ui/Text";
import classes from "./ImpactCalculator.module.scss";
import { clsx } from "clsx";
import { Link } from "@/components/ui/Link";
import { Plus } from "@/components/ui/Icon/Plus";
import { Minus } from "@/components/ui/Icon/Minus";
import { formatCurrency } from "@/utils/formatters";
import { ImpactCalculatorProps } from "./ImpactCalculatorProps";
import { Card } from "@/components/ui/Card";
import { useTranslation } from "@/hooks/useTranslation";
import type { ImpactCalculatorItem } from "@/types/components/ImpactCalculatorComponent";
import { AnimatePresence, LazyMotion, domAnimation } from "motion/react";
import * as m from "motion/react-m";

export function ImpactCalculator(props: ImpactCalculatorProps) {
  const { className, amount, config, isMonthly = false } = props;

  const t = useTranslation("impact_calculator");
  const tCommon = useTranslation("_common");
  const contentRef = useRef<HTMLDivElement>(null);

  const [isOpen, setIsOpen] = useState(false);

  const handleToggle = (ev: React.MouseEvent<HTMLButtonElement>) => {
    ev.stopPropagation();

    const shouldBeOpen = !isOpen;
    if (shouldBeOpen && contentRef.current) {
      contentRef.current.focus();
    }
    setIsOpen(shouldBeOpen);
  };

  const toggleText = isOpen ? tCommon("show-less") : tCommon("show-more");
  const Icon = isOpen ? Minus : Plus;

  const amountFormatted = formatCurrency(amount);

  const handleCardClick = () => {
    if (!isOpen) {
      setIsOpen(true);
    }
  };

  const calculatedAmount = isMonthly ? amount * 12 : amount;

  const lastItem = config["impact-calculator-items"].reduce(
    (prev, curr) => {
      if (curr["amount-start"] <= calculatedAmount) {
        return curr;
      }
      return prev;
    },
    null as ImpactCalculatorItem | null,
  );

  const description = lastItem
    ? t(
        lastItem.description.i18n,
        {
          amount: amountFormatted,
        },
        {
          transform: (text: string) => {
            if (isMonthly) {
              return text.replaceAll(
                "Your donation",
                "Your donation over a year",
              );
            }
            return text;
          },
        },
      )
    : "";

  return (
    <LazyMotion features={domAnimation}>
      <Card
        onClick={handleCardClick}
        className={clsx(
          classes.card,
          className,
          isOpen && classes.open,
          lastItem?.image && classes.hasImage,
        )}
      >
        <div className={classes.header}>
          <Heading as="h2" size="xl">
            {t(config["impact-calculator-title"].i18n)}
          </Heading>
          <AnimatePresence initial={false} mode="popLayout">
            {!isOpen && (
              <m.div
                key="header-intro"
                className={clsx(classes.headerText, classes.htmlFormatted)}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 10 }}
                transition={{ duration: 0.2 }}
              >
                {t(config["impact-calculator-intro"].i18n)}
              </m.div>
            )}
          </AnimatePresence>
        </div>

        <m.div
          animate={{ height: isOpen ? "auto" : 0 }}
          className={classes.collapse}
          id="impact-calculator-content"
          tabIndex={-1}
          ref={contentRef}
          role="region"
        >
          <AnimatePresence initial={false} mode="wait">
            {isOpen && (
              <>
                <m.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.2 }}
                  className={classes.content}
                >
                  {lastItem && (
                    <div
                      className={clsx(
                        classes.textContent,
                        classes.htmlFormatted,
                      )}
                    >
                      {description}
                    </div>
                  )}
                  <Text className={classes.linkText}>
                    <Link
                      href={config["impact-calculator-link-url"]}
                      target="_blank"
                    >
                      {t(config["impact-calculator-link-text"].i18n)}
                    </Link>
                  </Text>
                </m.div>
                {lastItem?.image && (
                  <m.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0, transition: { delay: 0 } }}
                    transition={{ duration: 0.2, delay: 0.25 }}
                    className={classes.image}
                  >
                    <img src={lastItem.image.src} alt="" />
                  </m.div>
                )}
              </>
            )}
          </AnimatePresence>
        </m.div>

        <button
          type="button"
          className={classes.trigger}
          aria-label={toggleText as string}
          onClick={handleToggle}
          aria-expanded={isOpen}
          aria-controls="impact-calculator-content"
        >
          <Icon size={24} color="currentColor" />
        </button>
      </Card>
    </LazyMotion>
  );
}
